import React, { FC, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Icon, Input, SegmentedControl, SegmentedControlSize, ToggleHeight } from '@column/column-ui-kit';

import { DateInput } from '~/components/DateInput';
import { UserTransferFilterSnapshot } from '~/repositories';
import { FormElement, FormLabel } from '~/styles/Form';
import { isValidDate } from '~/util';
import { convertDateToRange, convertDatesToRange, parseDateTime } from '~/util/dateFilterHelpers';

interface DateFilterState {
  date?: string;
  time?: string;
  timeAm?: boolean;
}

const Content = styled.div`
  display: grid;
  grid-gap: 16px;
`;

const TimeWrapper = styled.div`
  position: relative;
  min-height: 16px;
`;

const TimeButton = styled(Button)<{ $isHidden?: boolean }>`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  transition:
    opacity 0.2s,
    color 0.2s;

  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

const TimeContent = styled(ToggleHeight)`
  --toggle-height-padding: 0;
`;

const TimeFields = styled.div`
  display: grid;
  grid-template-columns: auto 83px;
  grid-gap: 8px;
  align-items: center;
`;

const StyledSegmentedControl = styled(SegmentedControl)`
  button {
    padding: 5px 8px;
  }
`;

const Line = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(150)};
`;

export const TransferDateFilter: FC = () => {
  const [currentTab, setCurrentTab] = useState<'exact' | 'range'>('exact');
  const { watch, setValue } = useFormContext<Partial<UserTransferFilterSnapshot>>();

  const [date, setDate] = useState<Partial<DateFilterState>>({});
  const [startDate, setStartDate] = useState<Partial<DateFilterState>>({});
  const [endDate, setEndDate] = useState<Partial<DateFilterState>>({});

  const [dateShowTime, setDateShowTime] = useState<boolean>(false);
  const [startDateShowTime, setStartDateShowTime] = useState<boolean>(false);
  const [endDateShowTime, setEndDateShowTime] = useState<boolean>(false);

  useEffect(() => {
    const createdAtGte = watch('filters.createdAtGte');
    const createdAtLt = watch('filters.createdAtLt');

    if (createdAtGte && createdAtLt) {
      const startInfo = parseDateTime(createdAtGte);
      const endInfo = parseDateTime(createdAtLt, true);

      if (startInfo.date === endInfo.date) {
        setDate(startInfo);
        setCurrentTab('exact');
        setDateShowTime(true);
        setStartDate({});
        setEndDate({});
      } else {
        setDate({});
        setStartDate(startInfo);
        setStartDateShowTime(true);
        setEndDate(endInfo);
        setEndDateShowTime(true);
        setCurrentTab('range');
      }
    } else {
      setDate({});
      setStartDate({});
      setEndDate({});
      setCurrentTab('exact');
    }
  }, []);

  useEffect(() => {
    if (watch('filters.createdAtGte') === null) {
      setStartDate({ date: '' });
    }
    if (watch('filters.createdAtLt') === null) {
      setEndDate({ date: '' });
    }
    if (watch('filters.createdAtGte') === null && watch('filters.createdAtLt') === null) {
      setDate({ date: '' });
    }
  }, [watch('filters.createdAtGte'), watch('filters.createdAtLt')]);

  useEffect(() => {
    if (currentTab === 'exact') {
      const { date: dateString, time, timeAm } = date;

      const { createdAtGte, createdAtLt } = convertDateToRange(dateString, time, timeAm);

      if (
        createdAtGte &&
        createdAtLt &&
        watch('filters.createdAtGte') === createdAtGte &&
        watch('filters.createdAtLt') === createdAtLt
      ) {
        return;
      }

      if (createdAtGte && createdAtLt && isValidDate(new Date(createdAtGte)) && isValidDate(new Date(createdAtLt))) {
        setValue('filters.createdAtGte', createdAtGte);
        setValue('filters.createdAtLt', createdAtLt);
      } else {
        setValue('filters.createdAtGte', undefined);
        setValue('filters.createdAtLt', undefined);
      }
    } else {
      const { date: startDateString, time: startTime, timeAm: startTimeAM } = startDate;
      const { date: endDateString, time: endTime, timeAm: endTimeAM } = endDate;

      const { createdAtGte, createdAtLt } = convertDatesToRange({
        startDateString,
        startTime,
        startTimeAM,
        endDateString,
        endTime,
        endTimeAM,
      });

      if (
        createdAtGte &&
        createdAtLt &&
        watch('filters.createdAtGte') === createdAtGte &&
        watch('filters.createdAtLt') === createdAtLt
      ) {
        return;
      }

      if (createdAtGte && createdAtLt && isValidDate(new Date(createdAtGte)) && isValidDate(new Date(createdAtLt))) {
        setValue('filters.createdAtGte', createdAtGte);
        setValue('filters.createdAtLt', createdAtLt);
      } else {
        setValue('filters.createdAtGte', undefined);
        setValue('filters.createdAtLt', undefined);
      }
    }
  }, [date, startDate, endDate, currentTab]);

  const isValidTime = useCallback((value?: string) => {
    if (typeof value === 'undefined' || !value) {
      return true;
    }

    const timeValue = value?.replace(':', '');

    if (timeValue.length !== 4) {
      return false;
    }

    const numberValue = Number(timeValue);

    return numberValue >= 0 && numberValue <= 1259;
  }, []);

  const handleTimeChange = useCallback(
    (value: string, key: 'date' | 'startDate' | 'endDate') => {
      let formatted = value.replace(/[^0-9:]/g, '');

      if (formatted.length > 2 && !formatted.includes(':')) {
        formatted = `${formatted.substring(0, 2)}:${formatted.substring(2)}`;
      }

      formatted = formatted.slice(0, 5);

      if (key === 'date') {
        setDate((s) => ({ ...s, time: formatted }));

        return;
      }

      if (key === 'startDate') {
        setStartDate((s) => ({ ...s, time: formatted }));

        return;
      }

      if (key === 'endDate') {
        setEndDate((s) => ({ ...s, time: formatted }));

        return;
      }
    },
    [date, startDate, endDate]
  );

  return (
    <>
      <SegmentedControl
        options={[
          {
            label: 'Exact',
            value: 'exact',
          },
          {
            label: 'Range',
            value: 'range',
          },
        ]}
        fullWidth
        active={currentTab}
        onOptionChange={setCurrentTab}
      />
      {currentTab !== 'range' ? (
        <Content key="exact">
          <FormElement>
            <FormLabel>Created at</FormLabel>
            <DateInput
              value={date?.date}
              onChange={(value) => setDate((s) => (value ? { ...s, date: value } : { date: '' }))}
            />
          </FormElement>
          <TimeWrapper>
            <TimeButton
              variant="subtle-primary"
              size="small"
              type="button"
              onClick={() => setDateShowTime(true)}
              icon={<Icon.Plus />}
              $isHidden={dateShowTime}
            >
              Add time
            </TimeButton>
            <TimeContent isClose={!dateShowTime}>
              <FormElement>
                <FormLabel>Time</FormLabel>
                <TimeFields>
                  <Input
                    placeholder="00:00"
                    value={date?.time ?? ''}
                    onChange={(value) => handleTimeChange(value, 'date')}
                    hasError={!isValidTime(date?.time)}
                    size="small"
                  />
                  <StyledSegmentedControl
                    options={[
                      {
                        label: 'AM',
                        value: true,
                      },
                      {
                        label: 'PM',
                        value: false,
                      },
                    ]}
                    size={SegmentedControlSize.Small}
                    active={date.timeAm ?? true}
                    onOptionChange={(value: boolean | undefined) => setDate((s) => ({ ...s, timeAm: value }))}
                  />
                </TimeFields>
              </FormElement>
            </TimeContent>
          </TimeWrapper>
        </Content>
      ) : (
        <Content key="range">
          <FormElement>
            <FormLabel>Start Date</FormLabel>
            <DateInput value={startDate?.date} onChange={(value) => setStartDate((s) => ({ ...s, date: value }))} />
          </FormElement>
          <TimeWrapper>
            <TimeButton
              variant="subtle-primary"
              size="small"
              type="button"
              onClick={() => setStartDateShowTime(true)}
              icon={<Icon.Plus />}
              $isHidden={startDateShowTime}
            >
              Add time
            </TimeButton>
            <TimeContent isClose={!startDateShowTime}>
              <FormElement>
                <FormLabel>Time</FormLabel>
                <TimeFields>
                  <Input
                    placeholder="00:00"
                    value={startDate?.time ?? ''}
                    onChange={(value) => handleTimeChange(value, 'startDate')}
                    hasError={!isValidTime(startDate?.time)}
                    size="small"
                  />
                  <StyledSegmentedControl
                    options={[
                      {
                        label: 'AM',
                        value: true,
                      },
                      {
                        label: 'PM',
                        value: false,
                      },
                    ]}
                    size={SegmentedControlSize.Small}
                    active={startDate?.timeAm ?? true}
                    onOptionChange={(value: boolean | undefined) => setStartDate((s) => ({ ...s, timeAm: value }))}
                  />
                </TimeFields>
              </FormElement>
            </TimeContent>
          </TimeWrapper>

          <Line />

          <FormElement>
            <FormLabel>End Date</FormLabel>
            <DateInput value={endDate?.date} onChange={(value) => setEndDate((s) => ({ ...s, date: value }))} />
          </FormElement>
          <TimeWrapper>
            <TimeButton
              variant="subtle-primary"
              size="small"
              type="button"
              onClick={() => setEndDateShowTime(true)}
              icon={<Icon.Plus />}
              $isHidden={endDateShowTime}
            >
              Add time
            </TimeButton>
            <TimeContent isClose={!endDateShowTime}>
              <FormElement>
                <FormLabel>Time</FormLabel>
                <TimeFields>
                  <Input
                    placeholder="00:00"
                    value={endDate?.time ?? ''}
                    onChange={(value) => handleTimeChange(value, 'endDate')}
                    hasError={!isValidTime(endDate?.time)}
                    size="small"
                  />
                  <StyledSegmentedControl
                    options={[
                      {
                        label: 'AM',
                        value: true,
                      },
                      {
                        label: 'PM',
                        value: false,
                      },
                    ]}
                    size={SegmentedControlSize.Small}
                    active={endDate?.timeAm ?? true}
                    onOptionChange={(value: boolean | undefined) => setEndDate((s) => ({ ...s, timeAm: value }))}
                  />
                </TimeFields>
              </FormElement>
            </TimeContent>
          </TimeWrapper>
        </Content>
      )}
    </>
  );
};
